import type { PerformanceApiItem, ProductApiItem } from '@/app/lib/api/types-api'

import { mapProductToCard } from './mapProductToCard'
import type { ProductCard } from './toProductCards'

/**
 * Adapta un evento con forma `PerformanceApiItem` a la tarjeta del sitio —
 * `POST /resource/batch` («Vistos recientemente») y los listados relacionados
 * de la ficha de evento (`similar_performances`/`associated_performances`,
 * specs/043-pagina-de-producto), que comparten exactamente esa forma.
 *
 * **No** implementa lógica de negocio: normaliza la forma a `ProductApiItem` y
 * delega en `mapProductToCard`, que es el único sitio donde viven los estados de
 * compra (`resolvePurchaseState`, specs/019), el precio (`resolveEventPrice`,
 * specs/016), la fecha (`formatEventDate`, specs/017) y el saneado de imagen.
 * Así las tarjetas de "vistos recientemente" son idénticas a las de cualquier
 * otro listado (FR-014, FR-015). Si alguna vez hace falta reimplementar algo de
 * eso aquí, es señal de que la adaptación está mal.
 *
 * Diferencias absorbidas (verificadas contra el payload real de UAT):
 * - `genres` es array en ambos tipos (contrato final del backend — ver
 *   `ProductApiItem.genres`), así que viaja tal cual sin transformar; el
 *   primer/único género se resuelve más adelante con `resolveProductGenre`.
 * - **Sin** `dates_type` ⇒ `null`: `formatEventDate` cae en su rama de rango,
 *   que con `start_at` + `end_at` da "Del X al Y" y con solo `start_at`,
 *   "Desde el X".
 * - **Sin** `disable_purchase` ⇒ `false` (sin dato, evento no inhabilitado).
 * - `venue` trae campos extra (`id`, `nicename`, `url`, `address`) que se ignoran.
 * - Las promociones llegan **sin** `visible`, así que `filterVisiblePromotions`
 *   las descarta y estos eventos no pintan badge de promoción. Es lo correcto
 *   por defecto: sin ese campo no se puede afirmar que la promoción sea visible.
 *
 * Devuelve el `id` del evento junto a los datos de tarjeta: es lo que necesita
 * el botón "X" para dar de baja en el historial.
 */
export function mapPerformanceToCard(
  item: PerformanceApiItem,
  options?: { readonly preferVerticalImage?: boolean },
): ProductCard {
  return { id: item.id, ...mapProductToCard(performanceToProduct(item), options) }
}

/**
 * Normaliza un evento de `POST /resource/batch` a `ProductApiItem`, la forma que
 * consumen los mappers del sitio. Exportada aparte de `mapPerformanceToCard`
 * porque la analítica necesita el producto crudo, no la tarjeta: `items[]` de
 * `view_item_list` pide género, `min_price` numérico y `start_at`, que
 * `mapProductToCard` ya ha convertido en texto de presentación.
 */
export function performanceToProduct(item: PerformanceApiItem): ProductApiItem {
  return {
    id: item.id,
    title: item.title,
    url: item.url,
    image_square: item.image_square,
    image_vertical: item.image_vertical,
    image_panoramic: item.image_panoramic ?? null,
    min_price: item.min_price,
    original_price: item.original_price,
    promotions: item.promotions ?? [],
    has_app_promotions: item.has_app_promotions ?? false,
    stickers: item.stickers,
    venue: item.venue,
    genres: item.genres,
    purchase_text: item.purchase_text,
    start_at: item.start_at,
    end_at: item.end_at,
    dates_type: null,
    date_not_visible: item.date_not_visible,
    is_cancelled: item.is_cancelled,
    disable_purchase: item.disable_purchase ?? false,
    sold_out: item.sold_out,
    sale_end: item.sale_end,
    sale_start: item.sale_start,
    purchase_url: item.purchase_url,
  }
}
